import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

type ImagemImportada = {
  id: number;
  servico_id?: number | null;
  origem_tabela?: string | null;
  origem_id?: number | null;
  origem_campo?: string | null;
  origem_nome?: string | null;

  caminho?: string | null;
  caminho_original?: string | null;
  caminho_editado?: string | null;
  caminho_thumb?: string | null;

  url?: string | null;
  url_thumb?: string | null;
  url_original?: string | null;

  eh_capa?: boolean;
  status_imagem?: "pendente" | "aprovada" | "rejeitada" | string | null;
  updated_at?: string | null;
};

function campoLabel(campo?: string | null) {
  switch (campo) {
    case "foto_perfil":
      return "Foto de perfil";
    case "foto_espaco_1":
      return "Foto do espaço 1";
    case "foto_espaco_2":
      return "Foto do espaço 2";
    case "foto_espaco_3":
      return "Foto do espaço 3";
    case "foto_espaco_4":
      return "Foto do espaço 4";
    default:
      return campo || "-";
  }
}

function statusLabel(status?: string | null) {
  if (status === "aprovada") return "Aprovada";
  if (status === "rejeitada") return "Rejeitada";
  return "Pendente";
}

function statusClasses(status?: string | null) {
  if (status === "aprovada") {
    return "bg-green-100 text-green-700 border-green-200";
  }

  if (status === "rejeitada") {
    return "bg-red-100 text-red-700 border-red-200";
  }

  return "bg-yellow-100 text-yellow-700 border-yellow-200";
}

async function parseJsonSafe(res: Response) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Resposta inválida do servidor." };
  }
}

export default function ServicoImagensLista() {
  const location = useLocation();

  const [imagens, setImagens] = useState<ImagemImportada[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [busca, setBusca] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroOrigem, setFiltroOrigem] = useState("todos");

  const [importando, setImportando] = useState(false);
  const [mensagemSucesso, setMensagemSucesso] = useState("");

  async function carregarImagens() {
    setLoading(true);
    setErro("");

    try {
      const res = await fetch("/admin/servico-imagens/lista", {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });

      const data = await parseJsonSafe(res);

      if (!res.ok) {
        throw new Error(data?.message || "Erro ao carregar imagens.");
      }

      const lista = Array.isArray(data?.imagens) ? data.imagens : [];
      setImagens(lista);
    } catch (e: any) {
      setErro(e?.message || "Erro ao carregar imagens.");
    } finally {
      setLoading(false);
    }
  }

  async function importarImagens() {
    if (importando) return;

    setImportando(true);
    setErro("");
    setMensagemSucesso("");

    try {
        const res = await fetch("/admin/servico-imagens/importar", {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-TOKEN":
            document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        credentials: "same-origin",
        body: JSON.stringify({}),
        });

        const data = await parseJsonSafe(res);

        if (!res.ok) {
        throw new Error(data?.message || data?.error || "Erro ao importar imagens.");
        }

        setMensagemSucesso(data?.message || "Importação concluída com sucesso.");
        await carregarImagens();
    } catch (e: any) {
        setErro(e?.message || "Erro ao importar imagens.");
    } finally {
        setImportando(false);
    }
    }

  useEffect(() => {
    carregarImagens();
  }, []);

  const imagensFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    return imagens.filter((img) => {
      const matchBusca =
        !termo ||
        String(img.id).includes(termo) ||
        String(img.origem_id || "").includes(termo) ||
        String(img.origem_nome || "").toLowerCase().includes(termo) ||
        String(img.origem_tabela || "").toLowerCase().includes(termo) ||
        String(img.origem_campo || "").toLowerCase().includes(termo);

      const matchStatus =
        filtroStatus === "todos" || String(img.status_imagem || "") === filtroStatus;

      const matchOrigem =
        filtroOrigem === "todos" || String(img.origem_tabela || "") === filtroOrigem;

      return matchBusca && matchStatus && matchOrigem;
    });
  }, [imagens, busca, filtroStatus, filtroOrigem]);

  const totalPendente = imagens.filter((img) => img.status_imagem === "pendente").length;
  const totalAprovada = imagens.filter((img) => img.status_imagem === "aprovada").length;
  const totalRejeitada = imagens.filter((img) => img.status_imagem === "rejeitada").length;

  const returnTo = encodeURIComponent(location.pathname);

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 lg:px-8">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
                <h1 className="text-2xl font-bold tracking-tight">Imagens importadas</h1>
                <p className="mt-1 text-sm text-neutral-600">
                Escolha a imagem já existente no sistema para ajustar, publicar e padronizar.
                </p>
            </div>

            <div className="flex flex-wrap gap-3">
                <button
                type="button"
                onClick={importarImagens}
                disabled={importando}
                className="rounded-xl bg-black px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                {importando ? "Importando..." : "Importar imagens existentes"}
                </button>

                <button
                type="button"
                onClick={carregarImagens}
                className="rounded-xl border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50"
                >
                Atualizar lista
                </button>
            </div>
        </div>

        {erro ? (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {erro}
          </div>
        ) : null}

        {mensagemSucesso ? (
            <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                {mensagemSucesso}
            </div>
        ) : null}

        <div className="mb-6 grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
            <div className="text-sm text-neutral-500">Pendentes</div>
            <div className="mt-1 text-2xl font-bold">{totalPendente}</div>
          </div>

          <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
            <div className="text-sm text-neutral-500">Aprovadas</div>
            <div className="mt-1 text-2xl font-bold">{totalAprovada}</div>
          </div>

          <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
            <div className="text-sm text-neutral-500">Rejeitadas</div>
            <div className="mt-1 text-2xl font-bold">{totalRejeitada}</div>
          </div>
        </div>

        <div className="mb-6 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px_180px]">
            <div>
              <label className="mb-2 block text-sm font-medium">Buscar</label>
              <input
                type="text"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Nome, campo, origem ou ID"
                className="w-full rounded-xl border border-neutral-300 px-4 py-2.5 text-sm outline-none focus:border-black"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Status</label>
              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-4 py-2.5 text-sm outline-none focus:border-black"
              >
                <option value="todos">Todos</option>
                <option value="pendente">Pendente</option>
                <option value="aprovada">Aprovada</option>
                <option value="rejeitada">Rejeitada</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Origem</label>
              <select
                value={filtroOrigem}
                onChange={(e) => setFiltroOrigem(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-4 py-2.5 text-sm outline-none focus:border-black"
              >
                <option value="todos">Todas</option>
                <option value="users">Users</option>
                <option value="host_profiles">Host Profiles</option>
              </select>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
          {loading ? (
            <div className="p-6 text-sm text-neutral-500">Carregando imagens...</div>
          ) : imagensFiltradas.length === 0 ? (
            <div className="p-6 text-sm text-neutral-500">
              Nenhuma imagem encontrada com os filtros atuais.
            </div>
          ) : (
            <div className="divide-y divide-neutral-200">
              {imagensFiltradas.map((img) => {
                const editorUrl = `/admin/servico-imagens/editor?imagem_id=${img.id}&return_to=${returnTo}`;

                return (
                  <div
                    key={img.id}
                    className="flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between"
                  >
                    <div className="flex min-w-0 items-start gap-4">
                      <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-neutral-100">
                        <img
                          src={img.url_thumb || img.url || "/assets/img/placeholder.png"}
                          alt={`Imagem ${img.id}`}
                          className="h-full w-full object-cover"
                        />
                      </div>

                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-base font-semibold">
                            {img.origem_nome || "Sem nome"}
                          </h3>

                          <span
                            className={`rounded-full border px-2.5 py-1 text-xs font-medium ${statusClasses(
                              img.status_imagem
                            )}`}
                          >
                            {statusLabel(img.status_imagem)}
                          </span>

                          {img.eh_capa ? (
                            <span className="rounded-full bg-black px-2.5 py-1 text-xs font-medium text-white">
                              Capa
                            </span>
                          ) : null}
                        </div>

                        <div className="mt-2 space-y-1 text-sm text-neutral-600">
                          <div>
                            <strong>Imagem ID:</strong> {img.id}
                          </div>
                          <div>
                            <strong>Origem:</strong> {img.origem_tabela || "-"} /{" "}
                            {img.origem_id || "-"}
                          </div>
                          <div>
                            <strong>Campo:</strong> {campoLabel(img.origem_campo)}
                          </div>
                          <div>
                            <strong>Serviço ID:</strong> {img.servico_id || "-"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-3">
                      <Link
                        to={editorUrl}
                        className="inline-flex items-center justify-center rounded-xl bg-black px-4 py-2.5 text-sm font-medium text-white hover:opacity-90"
                      >
                        Editar imagem
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}